import { decryptWithDEK, encryptWithDEK } from "@/features/encryption/utils/encryption";

// Types for encrypted todo data
export type EncryptedTodoContent =
  | {
      encrypted_data: string;
      iv: string;
    }
  | {
      encrypted_data: null;
      iv: null;
    };

export interface DecryptedTodoContent {
  text: string;
}

/**
 * Encrypt todo text before sending to server
 * Only encrypts if encryption is enabled
 */
export async function encryptTodoContent(
  text: string | null | undefined,
  dek: CryptoKey
): Promise<EncryptedTodoContent> {
  try {
    const contentToEncrypt = JSON.stringify({
      text: text || "",
    });
    const { iv, ciphertext } = await encryptWithDEK(dek, contentToEncrypt);
    return {
      encrypted_data: ciphertext,
      iv,
    };
  } catch (error) {
    console.error("Failed to encrypt todo content:", error);
    return { encrypted_data: null, iv: null };
  }
}

/**
 * Decrypt todo text received from server
 * Only decrypts if encryption is enabled and data is available
 */
export async function decryptTodoContent(
  encryptedData: string,
  iv: string,
  dek: CryptoKey
): Promise<DecryptedTodoContent> {
  try {
    const decrypted = await decryptWithDEK(dek, encryptedData, iv);
    const parsed = JSON.parse(decrypted);

    return {
      text: parsed.text || "",
    };
  } catch (error) {
    console.error("Failed to decrypt todo content:", error);
    return {
      text: "",
    };
  }
}

/**
 * Process todo text by decrypting if encryption is enabled
 * Returns decrypted text or original text if no encryption
 */
export async function processTodoContent(
  todo: {
    text: string | null;
    encrypted_data: string | null;
    iv: string | null;
  },
  dek: CryptoKey | null | undefined
): Promise<{ text: string }> {
  if (dek && todo.encrypted_data && todo.iv) {
    return decryptTodoContent(todo.encrypted_data, todo.iv, dek);
  }
  return {
    text: todo.text || "",
  };
}

/**
 * Prepare encrypted content for database operations
 * Returns encrypted data if available, otherwise null values
 */
export async function prepareEncryptedTodoContent(
  text: string | null | undefined,
  dek: CryptoKey | null | undefined
): Promise<{
  encrypted_data: string | null;
  iv: string | null;
  clearContent: boolean;
}> {
  if (!dek) {
    return { encrypted_data: null, iv: null, clearContent: false };
  }

  const result = await encryptTodoContent(text, dek);
  return {
    ...result,
    clearContent: result.encrypted_data !== null && result.iv !== null,
  };
}
